import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { Menu, X } from "lucide-react";
import { GlitchButton } from "../ui/GlitchButton";
import { SocialButton, InstagramIcon, LinkedInIcon, GitHubIcon } from "../ui/SocialButton";
import gcLogo from "../../assets/gclogo2026.png";
import { SOCIAL_LINKS } from "../../lib/brand";
import { NAV_ITEMS } from "../../data/agencyData";

export function AgencyNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAgencyHome = location.pathname === "/";
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>(NAV_ITEMS[0].section);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Highlight the nav item for whichever section is currently under the header.
  useEffect(() => {
    if (!isAgencyHome) return;
    const sections = NAV_ITEMS.map((item) => document.getElementById(item.section)).filter(
      (el): el is HTMLElement => el !== null
    );
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveSection(visible.target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [isAgencyHome]);

  const goToSection = (sectionId: string) => {
    setIsOpen(false);
    const scroll = () => document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (isAgencyHome) {
      scroll();
    } else {
      navigate(`/#${sectionId}`);
      setTimeout(scroll, 100);
    }
  };

  return (
    <nav
      className="fixed left-0 top-0 w-full z-50"
      style={{
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(8.5px)",
        WebkitBackdropFilter: "blur(8.5px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.18)",
      }}
    >
      <div className="flex items-center justify-between px-4 lg:px-[116px] py-4 lg:py-[24px] max-w-[1920px] mx-auto">
        <div className="flex items-center gap-8">
          <button
            type="button"
            onClick={() => goToSection("landing")}
            className="shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#141414] rounded-lg"
            aria-label="Geekcow Creative Studio — back to top"
          >
            <img src={gcLogo} alt="Geekcow Creative Studio" className="h-[40px] lg:h-[60px] w-auto" />
          </button>

          <div className="hidden lg:flex items-center gap-1 xl:gap-2">
            {NAV_ITEMS.map((item) => (
              <GlitchButton
                key={item.section}
                text={item.label}
                active={activeSection === item.section}
                onClick={() => goToSection(item.section)}
                className="h-[47px] px-3 xl:px-4 font-medium text-[15px] xl:text-[16px] leading-[22.18px]"
              />
            ))}
          </div>
        </div>

        {/* Mobile trigger */}
        <div className="lg:hidden">
          <button
            className="p-2 text-[#141414]"
            onClick={() => setIsOpen(true)}
            aria-label="Open menu"
            aria-expanded={isOpen}
          >
            <Menu size={32} strokeWidth={2.5} />
          </button>

          <AnimatePresence>
            {isOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-40"
                  onClick={() => setIsOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, x: "-50%", y: -10 }}
                  animate={{ opacity: 1, scale: 1, x: "-50%", y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, x: "-50%", y: -10 }}
                  className="fixed left-1/2 top-[100px] w-[90%] max-w-[320px] max-h-[calc(100vh-120px)] overflow-y-auto bg-white/85 backdrop-blur-xl border border-white/40 rounded-[32px] p-6 z-50 shadow-2xl flex flex-col items-center gap-3"
                >
                  <button
                    onClick={() => setIsOpen(false)}
                    className="self-end -mt-2 -mr-1 p-1 text-[#141414]"
                    aria-label="Close menu"
                  >
                    <X size={22} strokeWidth={2.5} />
                  </button>

                  {NAV_ITEMS.map((item, i) => (
                    <button
                      key={item.section}
                      onClick={() => goToSection(item.section)}
                      className={`w-full py-3 rounded-full text-[18px] font-medium transition-colors ${
                        i === 0
                          ? "bg-[#141414] text-white hover:bg-black/90"
                          : "bg-white border border-[#E5E5E5] text-[#141414] hover:bg-gray-50"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}

                  <button
                    onClick={() => goToSection("contact")}
                    className="w-full py-4 rounded-full bg-[#10fd02] text-[#003e5b] font-bold text-[14px] tracking-[2px] uppercase hover:scale-105 transition-transform mt-1"
                  >
                    Contact
                  </button>

                  <Link
                    to="/olga"
                    onClick={() => setIsOpen(false)}
                    className="w-full py-3 rounded-full border border-[#141414] text-center text-[14px] font-medium text-[#141414] hover:bg-[#141414] hover:text-white transition-colors"
                  >
                    Match and Hire our Founder Olga
                  </Link>

                  <div className="flex gap-4 mt-2">
                    <SocialButton href={SOCIAL_LINKS.instagram} ariaLabel="Follow Geekcow on Instagram">
                      <InstagramIcon className="w-5 h-5" />
                    </SocialButton>
                    <SocialButton href={SOCIAL_LINKS.linkedinCompany} ariaLabel="Follow GeekCow Creative Studio on LinkedIn">
                      <LinkedInIcon className="w-5 h-5" />
                    </SocialButton>
                    <SocialButton href={SOCIAL_LINKS.github} ariaLabel="View GitHub profile">
                      <GitHubIcon className="w-5 h-5" />
                    </SocialButton>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Desktop right side */}
        <div className="hidden lg:flex items-center gap-6">
          <Link
            to="/olga"
            className="hidden xl:block text-[13px] font-medium text-[#003e5b] underline-offset-4 hover:underline whitespace-nowrap"
          >
            Hire our founder Olga
          </Link>
          <SocialButton href={SOCIAL_LINKS.instagram} ariaLabel="Follow Geekcow on Instagram">
            <InstagramIcon />
          </SocialButton>
          <SocialButton href={SOCIAL_LINKS.linkedinCompany} ariaLabel="Follow GeekCow Creative Studio on LinkedIn">
            <LinkedInIcon />
          </SocialButton>
          <SocialButton href={SOCIAL_LINKS.github} ariaLabel="View GitHub profile">
            <GitHubIcon />
          </SocialButton>

          <button
            onClick={() => goToSection("contact")}
            className="bg-[#10fd02] h-[56.59px] w-[182px] rounded-full flex items-center justify-center cursor-pointer hover:scale-105 transition-transform shadow-lg"
          >
            <span className="font-bold text-[12.6px] leading-[12.6px] tracking-[2.1px] uppercase text-[#003e5b]">
              Contact
            </span>
          </button>
        </div>
      </div>
    </nav>
  );
}
